import {
  CalendarCheckIcon,
  HandshakeIcon,
  HeartHandshakeIcon,
  PhoneCallIcon,
  ThumbsUpIcon,
} from 'lucide-react'
import type {
  ActivityCategory,
  ActivityDefinition,
  ActivityGoal,
  ActivityPlan,
} from '../types/activity'

export const activityDefinitions: ActivityDefinition[] = [
  {
    id: 'realtor-outreach',
    label: 'Realtor prospect outreach',
    shortLabel: 'Realtor outreach',
    unit: 'touches',
    description:
      'Calls, texts, and drop-bys to agents who are not referring to you yet.',
    coachingNote:
      'New agent relationships are the highest-leverage activity in your plan. Volume matters more than polish.',
    icon: PhoneCallIcon,
    tone: 'brand',
    logging: 'manual',
    cadence: 'daily',
    targetOptions: [10, 15, 20, 25, 30],
    defaultWeeklyTarget: 20,
    requiresContact: true,
  },
  {
    id: 'consumer-conversation',
    label: 'Consumer conversations',
    shortLabel: 'Consumer convos',
    unit: 'conversations',
    description:
      'Live conversations with borrowers and prospects about their financing.',
    coachingNote:
      'Count only two-way conversations. Voicemails belong in outreach, not here.',
    icon: HeartHandshakeIcon,
    tone: 'teal',
    logging: 'assisted',
    cadence: 'daily',
    targetOptions: [5, 8, 10, 15, 20],
    defaultWeeklyTarget: 10,
    requiresContact: true,
  },
  {
    id: 'social-engagement',
    label: 'Social engagement',
    shortLabel: 'Social posts',
    unit: 'engagements',
    description:
      'Comments, shares, and DMs on posts from partners and prospects.',
    coachingNote:
      'Engaging on an agent’s listing post is the warmest possible first touch. Tag a contact when it matters, or just log the volume.',
    icon: ThumbsUpIcon,
    tone: 'navy',
    logging: 'manual',
    cadence: 'daily',
    targetOptions: [5, 10, 15, 20, 25],
    defaultWeeklyTarget: 15,
    requiresContact: false,
  },
  {
    id: 'past-client-call',
    label: 'Past-client touches',
    shortLabel: 'Past clients',
    unit: 'calls',
    description:
      'Quarterly check-ins, mortgage-planning reviews, and escrow-analysis calls.',
    coachingNote:
      'Every past client should hear from you once a quarter. Reviews create refinance and referral volume.',
    icon: CalendarCheckIcon,
    tone: 'success',
    logging: 'assisted',
    cadence: 'daily',
    targetOptions: [3, 5, 8, 10, 12],
    defaultWeeklyTarget: 5,
    requiresContact: true,
  },
  {
    id: 'partner-meeting',
    label: 'Partner meetings',
    shortLabel: 'Partner meetings',
    unit: 'meetings',
    description:
      'Coffees, lunches, and office visits with existing referral partners.',
    coachingNote:
      'Face-to-face time is what converts a one-off referral into a steady pipeline.',
    icon: HandshakeIcon,
    tone: 'warn',
    logging: 'manual',
    cadence: 'weekly',
    targetOptions: [1, 2, 3, 4, 5],
    defaultWeeklyTarget: 3,
    requiresContact: true,
  },
]

export const activityById: Record<ActivityCategory, ActivityDefinition> =
  activityDefinitions.reduce(
    (map, definition) => {
      map[definition.id] = definition
      return map
    },
    {} as Record<ActivityCategory, ActivityDefinition>,
  )

export const focusOptions = [
  {
    id: 'grow-partners',
    label: 'Grow new referral partners',
    description:
      'Weighted toward realtor prospecting, social engagement, and partner meetings.',
    weights: {
      'realtor-outreach': 25,
      'consumer-conversation': 8,
      'social-engagement': 20,
      'past-client-call': 3,
      'partner-meeting': 4,
    } as Record<ActivityCategory, number>,
  },
  {
    id: 'deepen-partners',
    label: 'Deepen existing partners',
    description:
      'Weighted toward partner meetings, co-marketing, and steady touches.',
    weights: {
      'realtor-outreach': 10,
      'consumer-conversation': 10,
      'social-engagement': 15,
      'past-client-call': 5,
      'partner-meeting': 5,
    } as Record<ActivityCategory, number>,
  },
  {
    id: 'past-client-base',
    label: 'Mine my past-client base',
    description:
      'Weighted toward quarterly touches, planning reviews, and escrow analysis.',
    weights: {
      'realtor-outreach': 8,
      'consumer-conversation': 12,
      'social-engagement': 10,
      'past-client-call': 12,
      'partner-meeting': 2,
    } as Record<ActivityCategory, number>,
  },
]

export const defaultPlan: ActivityPlan = {
  focus: 'grow-partners',
  workdays: 5,
  onboarded: true,
  lastUpdatedLabel: 'Updated 12 days ago',
  goals: [
    { category: 'realtor-outreach', enabled: true, weeklyTarget: 25 },
    { category: 'consumer-conversation', enabled: true, weeklyTarget: 10 },
    { category: 'social-engagement', enabled: true, weeklyTarget: 15 },
    { category: 'past-client-call', enabled: true, weeklyTarget: 5 },
    { category: 'partner-meeting', enabled: true, weeklyTarget: 3 },
  ],
}

/** A brand-new officer: nothing chosen, nothing logged, setup not completed. */
export const freshPlan: ActivityPlan = {
  focus: '',
  workdays: 5,
  onboarded: false,
  lastUpdatedLabel: 'Not set up yet',
  goals: activityDefinitions.map((definition) => ({
    category: definition.id,
    enabled: false,
    weeklyTarget: definition.defaultWeeklyTarget,
  })),
}

export function goalsForFocus(focusId: string): ActivityGoal[] {
  const focus = focusOptions.find((option) => option.id === focusId)
  return activityDefinitions.map((definition) => ({
    category: definition.id,
    enabled: true,
    weeklyTarget: focus
      ? focus.weights[definition.id]
      : definition.defaultWeeklyTarget,
  }))
}
